import React, { useEffect, useState } from "react";
import { getSpecialReleaseFormAdmin } from "../../services/specialReleaseFormService";
import MonthDayYearPicker from "../component-tools/MonthDayYearPicker";
import { getSafePickerDate, toSafeDateString } from "../../services/helpers";

export interface PersonalInformation {
  firstName: string;
  lastName: string;
  middleName: string;
  email: string;
  dateOfBirth: Date;
  phone: string;
  race: string;
  sex: string;
  socialSecurityNumber: string;
  canadianSocialSecurityNumber: string;
}

interface PersonalInformationProps {
  personalInformation: PersonalInformation | null;
  criminalOrderYears: number;
  validationErrors: { [key: string]: string };
  onInputChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: string,
  ) => void;
  onFormUpload: () => void;
  fileUploaded: boolean;
  employment: boolean;
  suran: boolean;
  showSpecialRelease: boolean;
}

const PersonalInformationForm: React.FC<PersonalInformationProps> = ({
  personalInformation,
  criminalOrderYears,
  validationErrors,
  onInputChange,
  onFormUpload,
  fileUploaded,
  employment,
  suran,
  showSpecialRelease,
}) => {
  const [georgiaFormURL, setGeorgiaFormURL] = useState<string>("");
  // const [newHampshireFormURL, setNewHampshireFormURL] = useState<string>("");
  const [nevadaFormURL, setNevadaFormURL] = useState<string>("");

  useEffect(() => {
    const fetchFormURLs = async () => {
      try {
        const georgiaURL =
          await getSpecialReleaseFormAdmin("GCIC Release Form");
        // const newHampshireURL = await getSpecialReleaseFormAdmin(
        //   "New Hampshire Statewide Release and Instructions",
        // );
        const nevadaURL = await getSpecialReleaseFormAdmin(
          "Nevada Statewide Consent Form",
        );
        setGeorgiaFormURL(georgiaURL);
        // setNewHampshireFormURL(newHampshireURL);
        setNevadaFormURL(nevadaURL);
      } catch (error) {
        console.error("Error fetching form URLs:", error);
      }
    };

    fetchFormURLs();
  }, []);

  return (
    <div className="data-container">
      <div className="field-stacked" style={{ flex: "0 0 30%" }}>
        <label className="field-label">First Legal Name:</label>
        <div className="field-input">
          <input
            className={`form-control ${
              validationErrors.firstName ? "is-invalid" : ""
            }`}
            type="text"
            value={personalInformation?.firstName || ""}
            onChange={(e) => onInputChange(e, "firstName")}
          />
          {validationErrors.firstName && (
            <div className="invalid-feedback">{validationErrors.firstName}</div>
          )}
        </div>
      </div>
      <div className="field-stacked" style={{ flex: "0 0 30%" }}>
        <label className="field-label">Middle Legal Name:</label>
        <div className="field-input">
          <input
            className={`form-control`}
            type="text"
            value={personalInformation?.middleName || ""}
            onChange={(e) => onInputChange(e, "middleName")}
          />
        </div>
      </div>
      <div className="field-stacked" style={{ flex: "0 0 30%" }}>
        <label className="field-label">Last Legal Name:</label>
        <div className="field-input">
          <input
            className={`form-control ${
              validationErrors.lastName ? "is-invalid" : ""
            }`}
            type="text"
            value={personalInformation?.lastName || ""}
            onChange={(e) => onInputChange(e, "lastName")}
          />
          {validationErrors.lastName && (
            <div className="invalid-feedback">{validationErrors.lastName}</div>
          )}
        </div>
      </div>
      <div className="field-stacked" style={{ flex: "0 0 35%" }}>
        <label className="field-label">Email:</label>
        <div className="field-input">
          <input
            className={`form-control ${
              validationErrors.email ? "is-invalid" : ""
            }`}
            type="text"
            value={personalInformation?.email || ""}
            onChange={(e) => onInputChange(e, "email")}
            disabled={!suran}
          />
          {validationErrors.email && (
            <div className="invalid-feedback">{validationErrors.email}</div>
          )}
        </div>
      </div>
      <div className="field-stacked" style={{ flex: "0 0 30%" }}>
        <label className="field-label">Date of Birth:</label>
        <div className="field-input">
          <MonthDayYearPicker
            value={
              personalInformation?.dateOfBirth
                ? getSafePickerDate(personalInformation.dateOfBirth)
                : new Date()
            }
            onChange={(newDate) =>
              onInputChange(
                { target: { value: toSafeDateString(newDate) } } as any,
                "dateOfBirth",
              )
            }
          />
          {validationErrors.dateOfBirth && (
            <div className="invalid-feedback" style={{ display: "block" }}>
              {validationErrors.dateOfBirth}
            </div>
          )}
        </div>
      </div>
      <div className="field-stacked" style={{ flex: "0 0 20%" }}>
        <label className="field-label">Phone:</label>
        <div className="field-input">
          <input
            className={`form-control ${
              validationErrors.phone ? "is-invalid" : ""
            }`}
            type="text"
            value={personalInformation?.phone}
            onChange={(e) => onInputChange(e, "phone")}
          />
          {validationErrors.phone && (
            <div className="invalid-feedback">{validationErrors.phone}</div>
          )}
        </div>
      </div>
      <div className="field-stacked" style={{ flex: "0 0 20%" }}>
        <label className="field-label">Race:</label>
        <div className="field-input">
          <input
            className={`form-control ${
              validationErrors.race ? "is-invalid" : ""
            }`}
            type="text"
            value={personalInformation?.race}
            onChange={(e) => onInputChange(e, "race")}
          />
          {validationErrors.race && (
            <div className="invalid-feedback">{validationErrors.race}</div>
          )}
        </div>
      </div>
      <div className="field-stacked" style={{ flex: "0 0 20%" }}>
        <label className="field-label">Sex:</label>
        <div className="field-input">
          <input
            className={`form-control ${
              validationErrors.sex ? "is-invalid" : ""
            }`}
            type="text"
            value={personalInformation?.sex}
            onChange={(e) => onInputChange(e, "sex")}
          />
          {validationErrors.sex && (
            <div className="invalid-feedback">{validationErrors.sex}</div>
          )}
        </div>
      </div>
      <div className="field-stacked" style={{ flex: "0 0 20%" }}>
        <label className="field-label">Social Security Number:</label>
        <div className="field-input">
          <input
            className={`form-control ${
              validationErrors.socialSecurityNumber ? "is-invalid" : ""
            }`}
            type="text"
            value={personalInformation?.socialSecurityNumber}
            onChange={(e) => onInputChange(e, "socialSecurityNumber")}
          />
          {validationErrors.socialSecurityNumber && (
            <div className="invalid-feedback">
              {validationErrors.socialSecurityNumber}
            </div>
          )}
        </div>
      </div>
      <div className="field-stacked" style={{ flex: "0 0 25%" }}>
        <label className="field-label">Canadian Social Insurance Number:</label>
        <div className="field-input">
          <input
            className={`form-control ${
              validationErrors.canadianSocialSecurityNumber ? "is-invalid" : ""
            }`}
            type="text"
            value={personalInformation?.canadianSocialSecurityNumber}
            onChange={(e) => onInputChange(e, "canadianSocialSecurityNumber")}
          />
          {validationErrors.canadianSocialSecurityNumber && (
            <div className="invalid-feedback">
              {validationErrors.canadianSocialSecurityNumber}
            </div>
          )}
        </div>
      </div>
      {showSpecialRelease && (
        <>
          <div className="text-container" style={{ flex: "0 0 60%" }}>
            {employment
              ? "If you currently live in, have resided in, or have worked in "
              : "If you currently live in, or have resided in "}
            <a href={georgiaFormURL} target="_blank" rel="noopener noreferrer">
              Georgia
            </a>{" "}
            {/* ,{" "}
            <a
              href={newHampshireFormURL}
              target="_blank"
              rel="noopener noreferrer"
            >
              New Hampshire
            </a>{" "} */}
            , or{" "}
            <a href={nevadaFormURL} target="_blank" rel="noopener noreferrer">
              Nevada
            </a>{" "}
            within the past {criminalOrderYears} years, a special release form
            is required. Please download the form by clicking on your state and
            upload the completed document once finished.{" "}
            <b>Please only upload PDF file types, no images.</b>
          </div>
          <div>
            {fileUploaded ? (
              <button className="oxbtn btn-grey">
                <i className="bi bi-check2-circle icon-large"></i>
              </button>
            ) : (
              <button className="oxbtn btn-grey" onClick={() => onFormUpload()}>
                Upload Form
              </button>
            )}
          </div>
          {validationErrors.specialReleaseForm && (
            <div className="invalid-feedback" style={{ display: "block" }}>
              {validationErrors.specialReleaseForm}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PersonalInformationForm;
